import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Award,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  MapPin,
  Star,
  User,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import {
  getLatestReview,
  getStatusClasses,
  getVisitActionLabel,
  getVisitAddress,
  getVisitDependantName,
  getVisitPhone,
  getVisitStart,
  hasGpsCheckIn,
  normalizeVisitStatus,
  REVIEWED_STATUSES,
  sortVisitsNewestFirst,
  sortVisitsOldestFirst,
  formatWatDateTime,
  formatWatTime,
} from '../../lib/visitUtils';

const StatCard = ({ label, value, tone = 'default' }) => {
  const toneClass = tone === 'red' ? 'text-[#A33232]' : tone === 'green' ? 'text-emerald-700' : 'text-[#9F1D35]';
  return (
    <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4 sm:p-5">
      <p className={`font-serif text-3xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{label}</p>
    </div>
  );
};

const CareOfficerDashboard = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    visits,
    dependants,
    ratings,
    performance,
    loading,
    dataError,
  } = useApp();

  const officerId = currentUser?.id || currentUser?.profileId;

  const officerVisits = useMemo(() => {
    return sortVisitsOldestFirst(
      visits
        .filter((visit) => !officerId || visit.careOfficerId === officerId)
        .map((visit) => ({
          ...visit,
          dependant: dependants.find((item) => item.id === visit.dependantId),
        }))
    );
  }, [dependants, officerId, visits]);

  const now = new Date();
  const actionableVisits = officerVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Scheduled');
  const nextVisit = actionableVisits.find((visit) => {
    const start = getVisitStart(visit);
    return !start || start >= now || hasGpsCheckIn(visit);
  }) || actionableVisits[0];
  const reviewedVisits = sortVisitsNewestFirst(officerVisits.filter((visit) => REVIEWED_STATUSES.has(normalizeVisitStatus(visit.status)))).slice(0, 4);
  const submittedVisits = officerVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Submitted');
  const flaggedVisits = officerVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Flagged');
  const assignedDependantIds = new Set(officerVisits.filter((visit) => visit.status !== 'Cancelled').map((visit) => visit.dependantId).filter(Boolean));
  const completedVisits = officerVisits.filter((visit) => ['Submitted', 'Approved', 'Flagged', 'Rejected'].includes(normalizeVisitStatus(visit.status)));

  const myPerformance = performance?.find((item) => item.officerId === officerId) || {};
  const myRatings = ratings?.filter((rating) => rating.careOfficerId === officerId) || [];
  const ratingValue = myPerformance.rating || currentUser?.rating || (
    myRatings.length
      ? (myRatings.reduce((sum, item) => sum + (Number(item.subscriberRating?.score) || 0), 0) / myRatings.length).toFixed(1)
      : 0
  );

  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EE] px-4 py-10" data-testid="care-officer-dashboard">
        <div className="mx-auto max-w-7xl rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-8 text-[#5A5A5A]">Loading your visits...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="care-officer-dashboard">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-[#9F1D35] text-lg font-semibold text-[#FAFAF7] sm:h-16 sm:w-16 sm:text-xl">
                {currentUser?.firstName?.[0] || 'M'}{currentUser?.lastName?.[0] || ''}
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">{greeting},</p>
                <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] sm:text-3xl">
                  {currentUser?.firstName || 'Care Officer'} {currentUser?.lastName || ''}
                </h1>
                <p className="mt-1 text-sm text-[#5A5A5A]">
                  {nextVisit ? 'Your next actionable visit is ready below.' : 'No actionable visits assigned right now.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-full border border-[#D8D5D0] bg-[#F5E8EA] px-4 py-2 text-[#9F1D35]">
                <Award className="h-4 w-4" />
                <span className="text-sm font-medium">{myPerformance.tier || currentUser?.performanceTier || 'Active'} Care Officer</span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-[#E8E6E3] bg-[#FAFAF7] px-4 py-2">
                <Star className="h-4 w-4 fill-[#C9A961] text-[#C9A961]" />
                <span className="text-sm font-medium text-[#1A1A1A]">{ratingValue || '-'} Rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
        {dataError && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {dataError}
          </div>
        )}

        <section className="mb-6 grid grid-cols-2 gap-3 sm:gap-5 lg:mb-8 lg:grid-cols-4">
          <StatCard label="Scheduled" value={actionableVisits.length} />
          <StatCard label="Submitted" value={submittedVisits.length} />
          <StatCard label="Flagged" value={flaggedVisits.length} tone="red" />
          <StatCard label="Assignments" value={assignedDependantIds.size} />
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
            <div className="flex flex-col gap-3 border-b border-[#E8E6E3] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Next Visit</h2>
                <p className="text-sm text-[#5A5A5A]">Scheduled visits stay scheduled until you submit the completed checklist.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/schedule')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#9F1D35] px-4 py-2 text-sm font-medium text-[#9F1D35]"
              >
                <Calendar className="h-4 w-4" />
                Full Schedule
              </button>
            </div>

            {nextVisit ? (
              <article className="p-4 sm:p-6">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`rounded-full border px-3 py-1 text-xs font-medium ${getStatusClasses(nextVisit.status)}`}>
                        {hasGpsCheckIn(nextVisit) ? 'Checked in' : nextVisit.status}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm text-[#5A5A5A]">
                        <Clock className="h-4 w-4" />
                        {formatWatDateTime(getVisitStart(nextVisit))}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl font-semibold text-[#1A1A1A]">
                        {getVisitDependantName(nextVisit, nextVisit.dependant)}
                      </h3>
                      <p className="mt-2 flex items-start gap-2 text-sm text-[#5A5A5A]">
                        <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        {getVisitAddress(nextVisit, nextVisit.dependant)}
                      </p>
                      <p className="mt-2 flex items-center gap-2 text-sm text-[#5A5A5A]">
                        <User className="h-4 w-4" />
                        {getVisitPhone(nextVisit, nextVisit.dependant)}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:min-w-[180px]">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                      onClick={() => navigate(`/visits/${nextVisit.id}/checklist`)}
                    >
                      <FileText className="h-4 w-4" />
                      {getVisitActionLabel(nextVisit)}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D8D5D0] px-5 py-3 text-sm font-medium text-[#1A1A1A]"
                      onClick={() => navigate(`/visits/${nextVisit.id}`)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </article>
            ) : (
              <div className="p-10 text-center sm:p-12">
                <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
                <h3 className="mb-2 text-lg font-semibold text-[#1A1A1A]">No upcoming visit to start</h3>
                <p className="text-[#5A5A5A]">Approved schedules will appear here once they are assigned.</p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4 sm:p-6">
            <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Performance</h2>
            <div className="mt-5 grid gap-4">
              <div>
                <p className="text-sm text-[#5A5A5A]">Completed reports</p>
                <p className="mt-1 font-serif text-3xl font-semibold tabular-nums text-[#9F1D35]">{completedVisits.length}</p>
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Average rating</p>
                <p className="mt-1 font-serif text-3xl font-semibold tabular-nums text-[#9F1D35]">{ratingValue || '-'}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
          <div className="flex flex-col gap-3 border-b border-[#E8E6E3] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Recent Reviews</h2>
              <p className="text-sm text-[#5A5A5A]">Flagged, approved, and rejected visits from supervisors.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/visits')}
              className="rounded-full border border-[#9F1D35] px-4 py-2 text-sm font-medium text-[#9F1D35]"
            >
              Show More
            </button>
          </div>
          <div className="divide-y divide-[#E8E6E3]">
            {reviewedVisits.map((visit) => {
              const latestReview = getLatestReview(visit);
              return (
                <article key={visit.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-[#1A1A1A]">{getVisitDependantName(visit, visit.dependant)}</h3>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>{visit.status}</span>
                    </div>
                    <p className="mt-1 text-sm text-[#5A5A5A]">{formatWatDateTime(getVisitStart(visit))}</p>
                    {latestReview?.comments && (
                      <p className="mt-2 line-clamp-2 text-sm text-[#5A5A5A]">
                        <AlertTriangle className="mr-1 inline h-4 w-4 text-[#A33232]" />
                        {latestReview.comments}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/visits/${visit.id}`)}
                    className={`rounded-full px-4 py-2 text-sm font-medium ${
                      visit.status === 'Flagged'
                        ? 'bg-[#A33232] text-[#FAFAF7]'
                        : visit.status === 'Approved'
                          ? 'bg-emerald-600 text-white'
                          : 'border border-[#D8D5D0] text-[#1A1A1A]'
                    }`}
                  >
                    View Review
                  </button>
                </article>
              );
            })}
            {reviewedVisits.length === 0 && (
              <div className="p-8 text-sm text-[#5A5A5A]">Reviewed visits will appear here after supervisors approve, flag, or reject submitted reports.</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default CareOfficerDashboard;
